<template>
  <div id="app" class="container-fluid">
    <!-- Header with Patient Info -->
    <full_head />

    <!-- Main Content Grid -->
    <div class="main-content">
      <div class="history-grid">
        <!-- Medical History Card -->
        <div class="card">
          <div class="card-header">
            <h3><i class="fas fa-clipboard-list"></i> Medical History</h3>
          </div>
          <div class="card-body">
            <div class="medical-history-section">
              <h4><i class="fas fa-allergies"></i> Medical Conditions</h4>
              <div class="condition-list">
                <div
                  v-for="(condition, index) in medicalHistory.conditions"
                  :key="index"
                  class="condition-item"
                >
                  <span class="condition-name">{{ condition.name }}</span>
                  <span class="condition-status" :class="condition.status">
                    {{ condition.status }}
                  </span>
                  <span class="condition-year" v-if="condition.year"
                    >Since {{ condition.year }}</span
                  >
                </div>
              </div>

              <h4><i class="fas fa-pills"></i> Current Medications</h4>
              <div v-if="medicalHistory.medications.length > 0">
                <div
                  v-for="(med, index) in medicalHistory.medications"
                  :key="index"
                  class="medication-item"
                >
                  <span class="med-name">{{ med.name }}</span>
                  <span class="med-dosage">{{ med.dosage }}</span>
                  <span class="med-purpose">{{ med.purpose }}</span>
                </div>
              </div>
              <p v-else class="no-data">No current medications</p>

              <h4><i class="fas fa-procedures"></i> Allergies</h4>
              <div class="allergies">
                <template v-if="medicalHistory.allergies.length > 0">
                  <span
                    v-for="(allergy, index) in medicalHistory.allergies"
                    :key="index"
                    class="allergy-tag"
                  >
                    {{ allergy }}
                  </span>
                </template>
                <span v-else class="no-data">No known allergies</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Family History Card -->
        <div class="card">
          <div class="card-header">
            <h3><i class="fas fa-family"></i> Family History</h3>
          </div>
          <div class="card-body">
            <div class="family-history">
              <div
                class="family-member"
                v-for="(history, member) in familyHistory"
                :key="member"
              >
                <h5>{{ member }}</h5>
                <div class="family-conditions">
                  <template v-if="history.length > 0">
                    <span
                      v-for="(condition, index) in history"
                      :key="index"
                      class="family-condition-tag"
                    >
                      {{ condition }}
                    </span>
                  </template>
                  <span v-else class="no-data">No significant history</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import full_head from "./full_head.vue";
export default {
  name: "MaternalProfile",
  data() {
    return {
      medicalHistory: {
        conditions: [
          { name: "Hypertension", status: "Controlled", year: 2020 },
          { name: "Asthma", status: "Intermittent", year: 2015 },
          { name: "Anemia", status: "Resolved", year: 2022 },
        ],
        medications: [
          {
            name: "Folic Acid",
            dosage: "400mg daily",
            purpose: "Pregnancy supplement",
          },
          {
            name: "Iron Supplement",
            dosage: "65mg daily",
            purpose: "Anemia prevention",
          },
        ],
        allergies: ["Penicillin", "Sulfa drugs"],
      },
      familyHistory: {
        Mother: ["Type 2 Diabetes", "Hypertension"],
        Father: ["Heart Disease"],
        Siblings: ["Asthma"],
        "Maternal Grandmother": ["Breast Cancer"],
        "Paternal Grandmother": ["Hypertension"],
      },
    };
  },
  components: {
    full_head,
  },
};
</script>

<style scoped>
.container-fluid {
  padding: 20px;
  background-color: #f8f9fa;
}

.main-content {
  margin-top: 20px;
}

.history-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
}

.card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  border: none;
  overflow: hidden;
  height: 100%;
}

.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 15px;
  border-bottom: 2px solid #dee2e6;
}

.card-header h3 {
  margin: 0;
  color: #495057;
  font-size: 18px;
  font-weight: 600;
}

.card-header h3 i {
  margin-right: 10px;
  color: #667eea;
}

.card-body {
  padding: 20px;
}

.medical-history-section h4 {
  color: #495057;
  margin: 20px 0 12px;
  font-size: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.medical-history-section h4 i {
  margin-right: 8px;
  color: #28a745;
}

.condition-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.condition-item {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid #667eea;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.condition-name {
  font-weight: 600;
  color: #212529;
  flex: 1;
  min-width: 150px;
  margin-bottom: 5px;
}

.condition-status {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 15px;
  font-weight: 600;
  margin: 0 8px 5px 0;
}

.condition-status.controlled {
  background: #d4edda;
  color: #155724;
}

.condition-status.resolved {
  background: #cce5ff;
  color: #004085;
}

.condition-status.intermittent {
  background: #fff3cd;
  color: #856404;
}

.condition-year {
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 5px;
}

.medication-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid #28a745;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.med-name {
  font-weight: 600;
  color: #212529;
  flex: 1;
  min-width: 120px;
  margin-bottom: 5px;
}

.med-dosage {
  color: #dc3545;
  font-weight: 600;
  margin: 0 10px 5px 0;
  font-size: 14px;
}

.med-purpose {
  color: #6c757d;
  font-size: 13px;
  margin-bottom: 5px;
}

.allergies {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.allergy-tag {
  background: #f8d7da;
  color: #721c24;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.family-history {
  padding: 15px;
}

.family-member {
  margin-bottom: 20px;
}

.family-member h5 {
  color: #495057;
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 600;
  padding-bottom: 5px;
  border-bottom: 1px solid #e9ecef;
}

.family-conditions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.family-condition-tag {
  background: #e9ecef;
  color: #495057;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 13px;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 15px 0;
  font-size: 14px;
}

/* Small screen optimizations */
@media (max-width: 576px) {
  .container-fluid {
    padding: 10px;
  }
  
  .history-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .card-body {
    padding: 15px;
  }
  
  .condition-item,
  .medication-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .condition-name,
  .med-name {
    min-width: 100%;
    margin-bottom: 0;
  }
  
  .condition-status,
  .med-dosage {
    margin: 0;
    align-self: flex-start;
  }
  
  .condition-year,
  .med-purpose {
    align-self: flex-start;
  }
  
  .card-header h3 {
    font-size: 16px;
  }
  
  .medical-history-section h4 {
    font-size: 15px;
  }
}

/* Very small screens */
@media (max-width: 380px) {
  .history-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .family-condition-tag,
  .allergy-tag {
    font-size: 12px;
    padding: 4px 10px;
  }
  
  .card-header {
    padding: 12px;
  }
  
  .card-body {
    padding: 12px;
  }
}
</style>